import { supabase } from './supabase';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? 'http://localhost:3000';

async function authedFetch(path: string, init: RequestInit = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      // Content-Type JSON solo se c'e' un body: Fastify risponde 400
      // (FST_ERR_CTP_EMPTY_JSON_BODY) alle DELETE con header JSON e body vuoto.
      ...(init.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// Come authedFetch ma ritorna un Blob (per download di file, es. export XLSX).
// Riusa il token supabase come authedFetch ma NON forza Content-Type JSON.
async function authedBlob(path: string, init: RequestInit = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.blob();
}

// Triggera il download lato browser di un Blob con un nome file dato.
function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ----- Auth (rotta pubblica: login con credenziali casella Merak) -----
export const authApi = {
  loginWithMailCredentials: async (email: string, password: string) => {
    const res = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      session?: { access_token: string; refresh_token: string };
      user?: { email: string };
    };
    if (!res.ok || !body.session) {
      throw new Error(body.error ?? `API ${res.status}`);
    }
    return body as {
      session: { access_token: string; refresh_token: string };
      user: { email: string };
    };
  },
};

// ----- Portal (rotta pubblica, il token se presente viene ignorato) -----
export const portalApi = {
  get: () => authedFetch('/portal'),
};

// ----- Accounts -----
export const accountsApi = {
  list: () => authedFetch('/api/v1/accounts'),
  create: (input: unknown) =>
    authedFetch('/api/v1/accounts', { method: 'POST', body: JSON.stringify(input) }),
  test: (input: unknown) =>
    authedFetch('/api/v1/accounts/test', { method: 'POST', body: JSON.stringify(input) }),
  remove: (id: string) => authedFetch(`/api/v1/accounts/${id}`, { method: 'DELETE' }),
  patch: (id: string, body: unknown) =>
    authedFetch(`/api/v1/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Forza la sincronizzazione IMAP di tutti gli account attivi (pulsante Sync).
  syncNow: () => authedFetch('/api/v1/accounts/sync', { method: 'POST' }),
};

// ----- Folders -----
export const foldersApi = {
  list: (accountId?: string) =>
    authedFetch(`/api/v1/folders${accountId ? `?account_id=${accountId}` : ''}`),
};

// ----- Messages -----
export const messagesApi = {
  list: (params: {
    account_id?: string;
    folder_id?: string;
    limit?: number;
    offset?: number;
    search?: string;
    unread?: boolean;
  } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) qs.set(k, String(v));
    });
    return authedFetch(`/api/v1/messages?${qs.toString()}`);
  },
  get: (id: string, includeBody = false) =>
    authedFetch(`/api/v1/messages/${id}${includeBody ? '?include_body=true' : ''}`),
  // Feed AI: messaggi recenti arricchiti (riassunto, tag, categoria) degli ultimi N giorni
  feed: (days = 15) => authedFetch(`/api/v1/messages/feed?days=${days}`),
  // Cruscotto: messaggi di una singola categoria (per la vista Dashboard)
  byCategory: (category: string) =>
    authedFetch(`/api/v1/messages/by-category?category=${encodeURIComponent(category)}`),
  // Ricerca full-text su TUTTA la posta Merak (tutte le cartelle della casella)
  search: (q: string, limit = 100) =>
    authedFetch(`/api/v1/messages/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  patch: (id: string, body: unknown) =>
    authedFetch(`/api/v1/messages/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  send: (input: unknown) =>
    authedFetch('/api/v1/messages/send', { method: 'POST', body: JSON.stringify(input) }),
  // Tag sul singolo messaggio: collega un tag esistente ({tagId}) o creane uno nuovo ({name})
  addTag: (messageId: string, body: { name: string } | { tagId: string }) =>
    authedFetch(`/api/v1/messages/${messageId}/tags`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeTag: (messageId: string, tagId: string) =>
    authedFetch(`/api/v1/messages/${messageId}/tags/${tagId}`, { method: 'DELETE' }),
  // Badge sul messaggio: assegna un badge esistente ({badgeId}) o creane uno
  // nuovo ({name,color}). Copia la mail in Badges/<nome> lato IMAP.
  addBadge: (messageId: string, body: { badgeId: string } | { name: string; color?: string }) =>
    authedFetch(`/api/v1/messages/${messageId}/badges`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeBadge: (messageId: string, badgeId: string) =>
    authedFetch(`/api/v1/messages/${messageId}/badges/${badgeId}`, { method: 'DELETE' }),
  // Rimozione tag con ambito ereditario: message = solo questa mail,
  // sender = tutte le mail dello stesso mittente, domain = stesso dominio
  // (elimina anche le regole tag corrispondenti).
  // Posta inviata dall'app + risposte a un messaggio.
  sentList: (limit = 200) => authedFetch(`/api/v1/messages/sent?limit=${limit}`),
  replies: (messageId: string) => authedFetch(`/api/v1/messages/${messageId}/replies`),
  removeTagScoped: (messageId: string, tagId: string, scope: 'message' | 'sender' | 'domain') =>
    authedFetch(`/api/v1/messages/${messageId}/tags/${tagId}/remove-scoped`, {
      method: 'POST',
      body: JSON.stringify({ scope }),
    }),
  // Segna/togli manualmente lo stato spam del messaggio (aggiorna is_spam + spam_score).
  setSpam: (id: string, isSpam: boolean) =>
    authedFetch(`/api/v1/messages/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_spam: isSpam }),
    }),
  // Imposta/azzera lo stato "Da rispondere" del messaggio.
  // status: 'todo' = da rispondere, 'done' = risposto, null = rimuovi flag.
  setReplyStatus: (id: string, status: 'todo' | 'done' | null) =>
    authedFetch(`/api/v1/messages/${id}/reply-status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  // Aggiornamento multiplo dello stato "Da rispondere" (azioni bulk).
  bulkReplyStatus: (ids: string[], status: 'todo' | 'done' | null) =>
    authedFetch('/api/v1/messages/bulk-reply-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    }),
  // Applicazione multipla di un tag a piu' messaggi (azione bulk).
  bulkAddTag: (ids: string[], tagId: string) =>
    authedFetch('/api/v1/messages/bulk-tags', {
      method: 'POST',
      body: JSON.stringify({ ids, tagId }),
    }),
  // Marca i messaggi selezionati come spam (senza blocklist).
  bulkSpam: (ids: string[]) =>
    authedFetch('/api/v1/messages/bulk-spam', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  // Blocca i mittenti dei messaggi selezionati (blocklist + is_spam + tag spam).
  bulkBlockSender: (ids: string[]) =>
    authedFetch('/api/v1/messages/bulk-block-sender', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  // Elenco dei messaggi marcati "Da rispondere" (ritorna { items }).
  listToReply: () => authedFetch('/api/v1/messages/to-reply'),
  // Scarica l'export XLSX dei messaggi "Da rispondere" e avvia il download nel browser.
  downloadToReplyXlsx: async () => {
    const blob = await authedBlob('/api/v1/messages/to-reply/export.xlsx');
    triggerBlobDownload(blob, 'da-rispondere.xlsx');
  },
  // Blocklist mittenti: elenco, sblocco, ri-scansione storico.
  blocklist: () => authedFetch('/api/v1/messages/blocklist'),
  // Toglie una mail dallo spam con ambito: message = solo questa,
  // sender/domain = mittente/dominio SEMPRE attendibile + pulizia retroattiva.
  unspamScoped: (id: string, scope: 'message' | 'sender' | 'domain') =>
    authedFetch(`/api/v1/messages/${id}/unspam`, {
      method: 'POST',
      body: JSON.stringify({ scope }),
    }),
  // Allowlist mittenti/domini attendibili.
  allowlist: () => authedFetch('/api/v1/messages/allowlist'),
  removeAllowlist: (id: string) =>
    authedFetch(`/api/v1/messages/allowlist/${id}`, { method: 'DELETE' }),
  unblock: (id: string) =>
    authedFetch(`/api/v1/messages/blocklist/${id}`, { method: 'DELETE' }),
  rescanSpam: () =>
    authedFetch('/api/v1/messages/rescan-spam', { method: 'POST' }),
};

// ----- AI (funzioni assistite: traduzione, rilevamento lingua, miglioramento bozza) -----
export const aiApi = {
  // Traduce un testo nella lingua target. Ritorna { translated, detected_source? }.
  translate: (input: { text: string; target_lang: string }) =>
    authedFetch('/api/v1/ai/translate', {
      method: 'POST',
      body: JSON.stringify(input),
    }) as Promise<{ translated: string; detected_source?: string }>,
  // Rileva la lingua di un testo. Ritorna { lang }.
  detectLanguage: (input: { text: string }) =>
    authedFetch('/api/v1/ai/detect-language', {
      method: 'POST',
      body: JSON.stringify(input),
    }) as Promise<{ lang: string }>,
  // Migliora una bozza di risposta (opzionalmente con originale e tono). Ritorna { improved }.
  improveReply: (input: { draft: string; original?: string; tone?: string }) =>
    authedFetch('/api/v1/ai/improve-reply', {
      method: 'POST',
      body: JSON.stringify(input),
    }) as Promise<{ improved: string }>,
};

// ----- Rules (regole automazione) -----
export const rulesApi = {
  list: () => authedFetch('/api/v1/rules'),
  create: (input: unknown) =>
    authedFetch('/api/v1/rules', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, body: unknown) =>
    authedFetch(`/api/v1/rules/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => authedFetch(`/api/v1/rules/${id}`, { method: 'DELETE' }),
  runOn: (messageId: string) =>
    authedFetch(`/api/v1/rules/run-on/${messageId}`, { method: 'POST' }),
};

// ----- Tags (etichette) -----
export const tagsApi = {
  list: () => authedFetch('/api/v1/tags'),
  // Elenco badge (kind='badge'); garantisce i predefiniti Alert/Da rispondere.
  badges: () => authedFetch('/api/v1/tags/badges'),
  create: (input: {
    name: string;
    color: string;
    icon?: string | null;
    parent_id?: string | null;
    is_ai_managed?: boolean;
  }) => authedFetch('/api/v1/tags', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, body: unknown) =>
    authedFetch(`/api/v1/tags/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => authedFetch(`/api/v1/tags/${id}`, { method: 'DELETE' }),
  // KPI aggregati dei tag: categorie + tag principali + totale messaggi
  kpi: () => authedFetch('/api/v1/tags/kpi'),
  // Regole tag ereditarie (mittente/dominio/contenuto): tutte le mail
  // corrispondenti, presenti e future, ereditano il tag.
  rules: () => authedFetch('/api/v1/tags/rules'),
  createRule: (input: { kind: 'sender' | 'domain' | 'content'; pattern: string; name?: string; tagId?: string }) =>
    authedFetch('/api/v1/tags/rules', { method: 'POST', body: JSON.stringify(input) }),
  removeRule: (id: string, removeTags = false) =>
    authedFetch(`/api/v1/tags/rules/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ removeTags }),
    }),
};

// ----- AI keys (BYOK: chiave OpenAI del cliente) -----
export const aiKeysApi = {
  get: () => authedFetch('/api/v1/ai-keys'),
  set: (provider: 'openai', key: string) =>
    authedFetch('/api/v1/ai-keys', {
      method: 'PUT',
      body: JSON.stringify({ provider, key }),
    }),
  remove: (provider: string) =>
    authedFetch(`/api/v1/ai-keys/${provider}`, { method: 'DELETE' }),
};

// ----- Usage (consumo AI per tenant) -----
export const usageApi = {
  get: (from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const q = qs.toString();
    return authedFetch(`/api/v1/usage${q ? `?${q}` : ''}`);
  },
};

// ----- CRM: Contatti (contatti unificati da LinkedIn / email / manuali) -----
export const contactsApi = {
  list: () => authedFetch('/api/v1/contacts'),
  create: (input: unknown) =>
    authedFetch('/api/v1/contacts', { method: 'POST', body: JSON.stringify(input) }),
  // Dettaglio: ritorna il contatto + saved_items + followups collegati
  get: (id: string) => authedFetch(`/api/v1/contacts/${id}`),
  // Timeline unificata (mail ricevute + elementi LinkedIn), ordinata per data desc
  timeline: (id: string) => authedFetch(`/api/v1/contacts/${id}/timeline`),
  patch: (id: string, body: unknown) =>
    authedFetch(`/api/v1/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => authedFetch(`/api/v1/contacts/${id}`, { method: 'DELETE' }),
};

// ----- CRM: Elementi salvati da LinkedIn (via estensione Chrome) -----
export const savedItemsApi = {
  list: (params: { kind?: string; contact_id?: string; q?: string } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    });
    const q = qs.toString();
    return authedFetch(`/api/v1/saved-items${q ? `?${q}` : ''}`);
  },
  remove: (id: string) => authedFetch(`/api/v1/saved-items/${id}`, { method: 'DELETE' }),
};

// ----- CRM: Follow-up (promemoria di ricontatto) -----
export const followupsApi = {
  list: (status?: string) =>
    authedFetch(`/api/v1/followups${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (input: { contact_id?: string; due_at: string; note: string; channel: string }) =>
    authedFetch('/api/v1/followups', { method: 'POST', body: JSON.stringify(input) }),
  patch: (id: string, body: { status?: string; due_at?: string; note?: string }) =>
    authedFetch(`/api/v1/followups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => authedFetch(`/api/v1/followups/${id}`, { method: 'DELETE' }),
};

// ----- CRM: Token per l'estensione Chrome (plaintext mostrato SOLO alla creazione) -----
export const extTokensApi = {
  list: () => authedFetch('/api/v1/ext/tokens'),
  create: (label: string) =>
    authedFetch('/api/v1/ext/tokens', { method: 'POST', body: JSON.stringify({ label }) }),
  revoke: (id: string) => authedFetch(`/api/v1/ext/tokens/${id}`, { method: 'DELETE' }),
};

// ----- CRM: Coda azioni copilota per l'estensione Chrome (task manuali) -----
export const extTasksApi = {
  list: (status?: string) =>
    authedFetch(`/api/v1/ext/tasks${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (input: {
    task_type: string;
    target_url: string;
    contact_id?: string | null;
    note?: string | null;
  }) => authedFetch('/api/v1/ext/tasks', { method: 'POST', body: JSON.stringify(input) }),
  remove: (id: string) => authedFetch(`/api/v1/ext/tasks/${id}`, { method: 'DELETE' }),
};


// ----- Parcheggio cancellazione 48h (le mail cancellate restano annullabili
// per 48 ore; poi il worker le elimina davvero dal server IMAP) -----
export const deletionApi = {
  // Parcheggia i messaggi (nessuna conferma: azione reversibile).
  mark: (messageIds: string[]) =>
    authedFetch('/api/v1/deletion/mark', {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds }),
    }),
  // Lista del parcheggio.
  parked: () => authedFetch('/api/v1/deletion/parked'),
  // FORZATURA cancellazione: doppia conferma esplicita (header + body).
  confirm: (ids: string[]) =>
    authedFetch('/api/v1/deletion/confirm', {
      method: 'POST',
      headers: { 'x-confirm-delete': 'true' },
      body: JSON.stringify({ ids, confirm: true }),
    }),
  // Annulla il parcheggio: la mail torna nel feed.
  cancel: (ids: string[]) =>
    authedFetch('/api/v1/deletion/cancel', { method: 'POST', body: JSON.stringify({ ids }) }),
  // Conteggio + prossima scadenza per il banner.
  alert: () => authedFetch('/api/v1/deletion/alert'),
};
